import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Login from './Login';
import Icon from '../AppIcon';

const ProtectedRoute = ({ children, requiredRole = null, fallbackComponent = null }) => {
  const { user, userProfile, loading, authError } = useAuth();

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={32} className="animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!user || !userProfile) {
    return <Login />;
  }

  // Role-based access control
  if (requiredRole && userProfile?.role !== requiredRole) {
    if (fallbackComponent) {
      return fallbackComponent;
    }

    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="mx-auto h-12 w-12 bg-destructive/10 rounded-lg flex items-center justify-center mb-4">
            <Icon name="ShieldX" size={24} className="text-destructive" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-2">
            Access Denied
          </h2>
          <p className="text-muted-foreground mb-6">
            You do not have permission to access this page. 
            {requiredRole && ` This page requires ${requiredRole} role.`}
          </p>
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-center justify-center space-x-2 mb-2">
              <Icon name="User" size={16} className="text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                Current Role: <span className="font-medium capitalize">{userProfile?.role || 'Unknown'}</span>
              </span>
            </div>
            <div className="flex items-center justify-center space-x-2">
              <Icon name="Shield" size={16} className="text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                Required Role: <span className="font-medium capitalize">{requiredRole}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Authentication error
  if (authError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="mx-auto h-12 w-12 bg-destructive/10 rounded-lg flex items-center justify-center mb-4">
            <Icon name="AlertTriangle" size={24} className="text-destructive" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-2">
            Authentication Error
          </h2>
          <p className="text-muted-foreground mb-6">
            {authError}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center space-x-2 text-sm text-primary hover:text-primary/80"
          >
            <Icon name="RefreshCw" size={16} />
            <span>Retry</span>
          </button>
        </div>
      </div>
    );
  }

  // Render protected content
  return children;
};

export default ProtectedRoute;